/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';

import { Button, CircularProgress, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import MicIcon from '@mui/icons-material/Mic';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import StopIcon from '@mui/icons-material/Stop';
import { enqueueSnackbar } from 'notistack';

import ConfirmationModal from './ConfirmationModal';
import { doctorSignature, toolbar, workspaceSignature } from '../../constants/reportEditor';
import ReportService from '../../services/report.service';
import SignatureService from '../../services/signature.service';
import { createEditorConfig, setupPasteHandler } from '../../utils/editorUtils';
import { sanitizeRichText } from '../../utils/sanitize';

const useStyles = makeStyles(() => ({
  '@global': {
    '.richtexteditor': {
      height: '100%',
    },
    '.richtexteditor iframe': {
      margin: '32px 16px 32px 48px !important',
      width: 'auto !important',
    },
    '.richtexteditor rte-powerby a': {
      cursor: 'default !important',
      opacity: '0 !important',
      pointerEvents: 'none !important',
      width: '0px !important',
    },
    '.richtexteditor rte-preview-framecontainer iframe': {
      margin: '0 !important',
      width: '100% !important',
    },
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
  buttonsContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
    padding: '16px 24px',
  },
  editorContainer: {
    backgroundColor: '#f3f3f3',
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 150px)',
    overflow: 'hidden',
    padding: '24px',
    position: 'relative',
    width: 'min(1000px,100vw - 48px)',
  },
  editorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 70px)',
    position: 'relative',
  },
  loaderWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  micButton: {
    bottom: '48px',
    position: 'absolute',
    right: '16px',
    zIndex: 1000,
  },
  recording: {
    animation: '$pulse 1.5s infinite',
    backgroundColor: '#ff4444',
    color: 'white',
  },
}));

function ReportEditor({ caseReport, onClose, caseId, isRecorder = false, user, workspaceSignatureId }) {
  const [loading, setLoading] = useState(false);
  const [reportEditor, setReportEditor] = useState(null);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [initialReportContent, setInitialReportContent] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const reportEditorRef = useRef(null);
  const audioChunksRef = useRef([]);
  const classes = useStyles();

  const fetchReportContent = async () => {
    setLoading(true);

    try {
      if (!caseReport?.id) {
        setTimeout(() => initEditor(), 1000);
        return;
      }

      const reportService = new ReportService();
      const report = await reportService.getCaseReport(caseReport.id, isRecorder);

      setTimeout(() => initEditor(report?.content), 1000);
    } catch (error) {
      console.error('Error fetching report:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportContent();
  }, [caseReport]);

  const initEditor = (content = '') => {
    const signaturesConfig = {
      customButtons: [
        {
          exec: 'footer',
          icon: 'rte-command-footer',
          name: 'footer',
        },
        {
          exec: 'signature',
          icon: 'rte-command-signature',
          name: 'signature',
        },
      ],
      svgCode_footer: doctorSignature,
      svgCode_signature: workspaceSignature,
      text_footer: 'Add Doctor Signature',
      text_signature: 'Add Workspace Header',
    };

    const headerFooterConfig = '{signature,footer}';

    const baseConfig = {
      ...signaturesConfig,
      editorResizeMode: 'none',
      enablecontextmenu: false,
      enableobjectresizing: true,
      enterKeyTag: 'div',
      height: '400px',
      maxImageSize: 15 * 1024 * 1024,
      showFloatParagraph: false,
      showPlusButton: false,
      showPoweredBy: false,
      showTagList: false,
      toolbar: 'headerToolbar',
      toolbar_headerToolbar: `${toolbar}${headerFooterConfig}#{undo,redo}`,
      width: '100%',
    };

    const editorConfig = createEditorConfig(baseConfig);

    try {
      const editor = new window.RichTextEditor(reportEditorRef.current, editorConfig);
      window.editor1 = editor;
      editor.setHTMLCode(content);
      const formattedContent = editor.getHTMLCode();
      setReportContent(formattedContent);
      setInitialReportContent(formattedContent);

      setUpEditorCommands(editor);

      editor.attachEvent('change', () => {
        setReportContent(editor.getHTMLCode());
      });

      setupPasteHandler(editor);

      setReportEditor(editor);
      setTimeout(() => {
        editor.clearHistory();
      }, 100);
    } catch (error) {
      console.error('Error initializing editor:', error);

      if (error.statusCode === 413 || error.message?.includes('request entity too large') || error.response?.status === 413) {
        enqueueSnackbar('Your report exceeds the maximum allowed size. Please reduce the content or the size or number of images and try again.', {
          autoHideDuration: 8000,
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred while saving your report. Please try again later.', { autoHideDuration: 8000, variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const setUpEditorCommands = (editor) => {
    editor.attachEvent('exec_command_signature', async (state) => {
      try {
        const signatureService = new SignatureService();
        const signature = workspaceSignatureId ? await signatureService.getSignatureById(workspaceSignatureId) : null;

        if (!signature?.content) {
          enqueueSnackbar(`Header not configured or empty. ${user.role !== 'creator' ? ' Please set it up from "Settings" page' : ''}`, {
            autoHideDuration: 5000,
            variant: 'info',
          });
          return;
        }

        const header = editor.document.createElement('div');
        header.innerHTML = signature.content;
        header.className = 'report-header';
        editor.selectDoc(true);
        editor.insertElement(header);

        setReportContent(editor.getHTMLCode());

        state.returnValue = true;
      } catch (error) {
        console.error('Error adding workspace header:', error);
      }
    });

    editor.attachEvent('exec_command_footer', async (state) => {
      try {
        const signatureService = new SignatureService();
        const signature = await signatureService.getDoctorSignature(user.id);

        if (!signature?.content) {
          enqueueSnackbar(`Signature not configured or empty. ${user.role !== 'creator' ? ' Please set it up from "My Account" page.' : ''}`, {
            autoHideDuration: 5000,
            variant: 'info',
          });
          return;
        }

        editor.selectDoc(false);
        const footer = editor.document.createElement('div');
        footer.innerHTML = signature.content;
        footer.className = 'report-footer';
        editor.insertElement(footer);
        editor.focus();

        setReportContent(editor.getHTMLCode());

        state.returnValue = true;
      } catch (error) {
        console.error('Error adding doctor signature:', error);
      }
    });
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (initialReportContent !== reportContent) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [initialReportContent, reportContent]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

          setIsProcessingAudio(true);

          const reportService = new ReportService();
          const { text } = await reportService.voiceTranscription(audioBlob);

          if (reportEditor && text) {
            // Handle the literal strings '<\n>' and '<\n\n>' that Deepgram returns
            let formattedText = text.replace(/<\\n\\n>/g, '<br /><br />');
            formattedText = formattedText.replace(/<\\n>/g, '<br />');

            // Also handle actual newlines if they exist
            formattedText = formattedText.replace(/\n\n/g, '<br /><br />');
            formattedText = formattedText.replace(/\n/g, '<br />');

            const container = reportEditor.document.createElement('span');
            container.innerHTML = formattedText;

            reportEditor.insertHTML(formattedText);
            reportEditor.focus();

            setReportContent(reportEditor.getHTMLCode());
          }
        } catch (error) {
          console.error('Error processing audio:', error);
          enqueueSnackbar('Error processing audio. Please try again.', {
            autoHideDuration: 5000,
            variant: 'error',
          });
        } finally {
          setIsProcessingAudio(false);
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Microphone permission error:', error);

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        enqueueSnackbar('Microphone access was denied. Please check your browser/system permissions and allow microphone access.', {
          autoHideDuration: 8000,
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Could not access microphone. Please check your device connections and permissions.', {
          autoHideDuration: 5000,
          variant: 'error',
        });
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  useEffect(() => {
    return () => {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder]);

  const handleSaveReport = async () => {
    if (!reportEditor || isSavingReport) return;

    try {
      setIsSavingReport(true);
      const reportService = new ReportService();
      const editorContent = reportEditor.getHTMLCode();
      const sanitizedContent = sanitizeRichText(editorContent);

      if (!caseReport) {
        await reportService.createCaseReport(sanitizedContent, caseId, isRecorder);
      } else {
        await reportService.updateCaseReport(caseReport, sanitizedContent, isRecorder);
      }

      enqueueSnackbar('Report saved successfully', { autoHideDuration: 10000, variant: 'success' });
      setInitialReportContent(editorContent);

      console.log('report-editor:saved');

      if (!isRecorder) onClose(true);
    } catch (error) {
      console.error('Error saving report:', error);

      if (error?.statusCode === 403) {
        enqueueSnackbar('You do not have permission to create or update this report', { autoHideDuration: 10000, variant: 'error' });
      } else if (error.statusCode === 413 || error.message?.includes('request entity too large') || error.response?.status === 413) {
        enqueueSnackbar('Your report exceeds the maximum allowed size. Please reduce the content or the size or number of images and try again.', {
          autoHideDuration: 8000,
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Failed to save report', { autoHideDuration: 10000, variant: 'error' });
      }
    } finally {
      setIsSavingReport(false);
    }
  };

  const handleClose = async () => {
    if (initialReportContent !== reportContent) {
      setConfirmModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = async () => {
    setConfirmModalOpen(false);
    onClose();
  };

  const handleCancelClose = async () => {
    setConfirmModalOpen(false);
  };

  const recorderIcon = () => {
    if (isProcessingAudio) {
      return (
        <div className={classes.micButton}>
          <CircularProgress />
        </div>
      );
    }

    return isRecording ? <StopIcon /> : <MicIcon />;
  };

  const handleRecorderClick = () => {
    if (isProcessingAudio) return;

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className={classes.editorContainer} style={{ height: isRecorder ? 'calc(100% - 48px)' : '' }}>
      {loading && (
        <div className={classes.loaderWrapper}>
          <CircularProgress />
        </div>
      )}

      <div className={classes.editorWrapper} style={{ display: loading ? 'none' : 'block' }}>
        {!loading &&
          (isProcessingAudio ? (
            <div className={classes.micButton}>
              <CircularProgress />
            </div>
          ) : (
            <Tooltip title={isRecording ? 'Stop Recording' : 'Start Voice Recording'}>
              <IconButton className={`${classes.micButton} ${isRecording ? classes.recording : ''}`} disabled={loading} onClick={handleRecorderClick}>
                {recorderIcon()}
              </IconButton>
            </Tooltip>
          ))}

        <div ref={reportEditorRef} />
      </div>

      <div className={classes.buttonsContainer}>
        {!isRecorder && (
          <Button onClick={handleClose} variant="outlined" color="primary">
            Close
          </Button>
        )}

        <Button
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSaveReport}
          variant="contained"
          color="primary"
          disabled={isSavingReport || loading || (!isRecorder && initialReportContent === reportContent)}>
          {isSavingReport ? 'Saving...' : 'Save Report'}
        </Button>
      </div>

      <ConfirmationModal
        open={confirmModalOpen}
        title="Confirm Close"
        message="You have unsaved changes. Are you sure you want to close the editor?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </div>
  );
}

export default ReportEditor;
